import { createRandomId } from "./utils.mjs";
import { sdkValidations } from "./cart/validation/shared.mjs";
let currentRefreshTokenPromise = undefined;
export const createStateInstance = ({ countryCode, currency, cartId, cartVersion, tokenString, refreshToken, language, siteUrl, psp, checkoutPageUrl, orderConfirmationPageUrl, onUnexpectedError, onShowOrderConfirmation, onBeforePurchase, onRedirect, onPostForm, onElementFocus, checkoutApiBaseUrl, atlasSessionId, translateApiMessage, getTrackingCookies, onServerSdkVersionMismatch, }) => {
    const listeners = [];
    const id = cartId !== null && cartId !== void 0 ? cartId : createRandomId();
    const version = cartVersion !== null && cartVersion !== void 0 ? cartVersion : createRandomId();
    const isNew = cartId ? false : true;
    let currentStateData = {
        id,
        version,
        createdAt: new Date().toISOString(),
        lines: [],
        giftCards: [],
        discountCodes: [],
        customer: {},
        fees: { shipping: undefined },
        isNew,
        // If we have a cart id we know that we'll load the cart
        isLoading: !isNew,
        leftToFreeShipping: null,
        bundleSavings: null,
        countryCode,
        currency,
        // We set these to false because at this point we can't know if the country code and currency are valid
        validCountryCode: false,
        validCurrency: false,
        isExport: false,
        language,
        siteUrl,
        checkoutPageUrl,
        orderConfirmationPageUrl,
        token: {
            tokenString,
            refreshToken: () => {
                if (currentRefreshTokenPromise !== undefined)
                    return currentRefreshTokenPromise;
                const newRefreshTokenPromise = refreshToken();
                newRefreshTokenPromise.then((newTokenString) => {
                    currentStateData.token.tokenString = newTokenString;
                });
                newRefreshTokenPromise.finally(() => {
                    currentRefreshTokenPromise = undefined;
                });
                currentRefreshTokenPromise = newRefreshTokenPromise;
                return newRefreshTokenPromise;
            },
        },
        // This is just the default state because at this point we don't know if we're ready to complete the purchase
        // or if the payment provider doesn't support completing the purchase by calling the SDKs completePurchase() method.
        completePurchaseReadyState: 'loading',
        paymentInProgressWithPaymentMethodId: undefined,
        psp,
        checkoutElements: {
            paymentVerification: undefined,
            takeover: undefined,
            express: undefined,
            buyButtonElementId: 'stickerapp-checkout-sdk-buy-button',
        },
        get trackingCookies() {
            return getTrackingCookies();
        },
        paymentProviderElementsOptions: {},
    };
    const instance = Object.assign(Object.assign({}, currentStateData), { onUnexpectedError,
        onShowOrderConfirmation,
        onBeforePurchase,
        onRedirect,
        onPostForm,
        onElementFocus,
        checkoutApiBaseUrl,
        onServerSdkVersionMismatch, completePurchaseHandler: undefined, atlasSessionId,
        translateApiMessage,
        psp,
        clear() {
            instance.update({
                id: createRandomId(),
                version: createRandomId(),
                createdAt: new Date().toISOString(),
                lines: [],
                giftCards: [],
                discountCodes: [],
                customer: {},
                isNew: true,
                isLoading: false,
                paymentInProgressWithPaymentMethodId: undefined,
                checkout: undefined,
                checkoutElements: {
                    paymentVerification: undefined,
                    takeover: undefined,
                    express: undefined,
                    buyButtonElementId: 'stickerapp-checkout-sdk-buy-button',
                },
            });
        },
        loadingDone() {
            if (!instance.isLoading) {
                return Promise.resolve();
            }
            return new Promise((resolve) => {
                const listener = (state) => {
                    if (!state.isLoading) {
                        resolve();
                        unsubscribe();
                    }
                };
                const unsubscribe = instance.onChange(listener);
            });
        },
        update(newStateOrUpdater) {
            const oldState = currentStateData;
            let newState;
            if (typeof newStateOrUpdater === 'function') {
                const clonedState = JSON.parse(JSON.stringify(currentStateData));
                newStateOrUpdater(clonedState);
                newState = clonedState;
            }
            else {
                newState = newStateOrUpdater;
            }
            newState = hideApplePayIfNeeded(newState);
            newState = handleCompletePurchaseReadyState(newState, currentStateData);
            Object.assign(instance, newState);
            currentStateData = Object.assign(Object.assign({}, currentStateData), newState);
            listeners.forEach((listener) => listener(instance, oldState));
        },
        onChange(callback) {
            listeners.push(callback);
            return () => {
                const index = listeners.indexOf(callback);
                if (index !== -1) {
                    listeners.splice(index, 1);
                }
            };
        } });
    return instance;
};
const handleCompletePurchaseReadyState = (state, currentStateData) => {
    var _a, _b;
    const selectedPaymentMethodId = ((_a = currentStateData.checkout) === null || _a === void 0 ? void 0 : _a.payment.$type) === 'checkoutPage' ? (_b = currentStateData.checkout.payment.selectedPaymentMethod) === null || _b === void 0 ? void 0 : _b.id : undefined;
    const paymentInProgressWithPaymentMethodId = 'paymentInProgressWithPaymentMethodId' in state
        ? state.paymentInProgressWithPaymentMethodId
        : currentStateData.paymentInProgressWithPaymentMethodId;
    if (state.isLoading) {
        return Object.assign(Object.assign({}, state), { completePurchaseReadyState: 'loading' });
    }
    else if (paymentInProgressWithPaymentMethodId && paymentInProgressWithPaymentMethodId === selectedPaymentMethodId) {
        return Object.assign(Object.assign({}, state), { completePurchaseReadyState: 'paymentInProgress' });
    }
    else {
        return Object.assign(Object.assign({}, state), { paymentInProgressWithPaymentMethodId: undefined, completePurchaseReadyState: readyState(currentStateData) });
    }
};
const hideApplePayIfNeeded = (state) => {
    var _a, _b;
    const supportsApplePay = !!(window === null || window === void 0 ? void 0 : window.ApplePaySession);
    if (!supportsApplePay) {
        const applePayId = 'applepay';
        if (((_b = (_a = state.checkout) === null || _a === void 0 ? void 0 : _a.payment) === null || _b === void 0 ? void 0 : _b.$type) === 'checkoutPage') {
            const applePayPaymentMethod = state.checkout.payment.paymentMethods.find((method) => method.id === applePayId);
            if (applePayPaymentMethod) {
                return Object.assign(Object.assign({}, state), { checkout: Object.assign(Object.assign({}, state.checkout), { payment: Object.assign(Object.assign({}, state.checkout.payment), { paymentMethods: state.checkout.payment.paymentMethods.filter((method) => method.id !== applePayId) }) }) });
            }
        }
    }
    return state;
};
const vatNumberState = (state) => {
    var _a, _b, _c, _d;
    return sdkValidations.vatNumber((_a = state.customer.company) === null || _a === void 0 ? void 0 : _a.isCompany, (_b = state.checkout) === null || _b === void 0 ? void 0 : _b.showVatInput, (_d = (_c = state.customer.company) === null || _c === void 0 ? void 0 : _c.vatNumber) === null || _d === void 0 ? void 0 : _d.isValid)
        .length > 0
        ? 'invalidVatNumber'
        : undefined;
};
const customerDataIsValid = (state) => {
    var _a, _b, _c, _d, _e, _f;
    const customer = state.customer;
    if (!customer) {
        return 'invalidCustomerData';
    }
    const addressValidation = sdkValidations.shippingAddress({
        customer,
        countryCode: state.countryCode,
    }, sdkValidations.skipAddressValidationFields((_a = state.psp) !== null && _a !== void 0 ? _a : 'adyen', state.countryCode));
    const billingValidation = sdkValidations.billingAddress({
        customer,
        countryCode: state.countryCode,
    }, sdkValidations.skipAddressValidationFields((_b = state.psp) !== null && _b !== void 0 ? _b : 'adyen', state.countryCode));
    const taxValid = sdkValidations.tax((_d = (_c = state.tax) === null || _c === void 0 ? void 0 : _c.error) === null || _d === void 0 ? void 0 : _d.code);
    const companyNameValid = sdkValidations.companyName((_e = customer.company) === null || _e === void 0 ? void 0 : _e.name, (_f = customer.company) === null || _f === void 0 ? void 0 : _f.isCompany);
    const emailValid = sdkValidations.email(customer.email);
    const phoneValid = sdkValidations.phoneNumber(customer.phoneNumber);
    const validations = [
        ...addressValidation,
        ...billingValidation,
        ...taxValid,
        ...companyNameValid,
        ...emailValid,
        ...phoneValid,
    ];
    if (validations.length > 0) {
        return 'invalidCustomerData';
    }
    return 'customerStateValid';
};
/**
 * Helper method to check if the checkout state has no validation errors for the given error codes.
 */
export const hasNoValidationErrors = (validation, errorCodes, prefix) => {
    if (!validation) {
        return true;
    }
    // if a prefix is provided, we check for any error codes that start with the prefix
    if (prefix) {
        return !validation.some((error) => error.code.startsWith(prefix + '_'));
    }
    return !validation.some((error) => errorCodes.includes(error.code));
};
const countryAndCurrencyIsValid = (state) => {
    // return hasNoValidationErrors(state, ['country_code_invalid', 'currency_invalid']);
    return state.validCountryCode && state.validCurrency;
};
const mapPaymentStatusToReadyState = (code) => {
    switch (code) {
        case 'payment_method_missing':
            return 'missingPaymentMethod';
        case 'payment_already_paid':
            return 'alreadyPaid';
        default:
            return 'ready';
    }
};
const readyState = (state) => {
    if (!countryAndCurrencyIsValid(state)) {
        return 'invalidCountryOrCurrency';
    }
    const customerState = customerDataIsValid(state);
    if (customerState !== 'customerStateValid') {
        return customerState;
    }
    const vatState = vatNumberState(state);
    if (vatState) {
        return vatState;
    }
    return checkoutReadyState(state.checkout);
};
const checkoutReadyState = (checkout) => {
    if (!sdkValidations.hasShippingMethod(checkout === null || checkout === void 0 ? void 0 : checkout.shipping)) {
        return 'missingShippingMethod';
    }
    const paymentStatus = sdkValidations.paymentStatus(checkout === null || checkout === void 0 ? void 0 : checkout.payment);
    return mapPaymentStatusToReadyState(paymentStatus);
};
//# sourceMappingURL=state.js.map